/**
 * Labstep
 *
 * @module components/Layout/Links/Vertical/State
 * @desc State-based vertical navigation links (no routing)
 */

import classnames from 'classnames';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { PremiumFeatureLockIcon } from 'labstep-web/core/PremiumFeature/LockIcon';
import type React from 'react';
import { useState } from 'react';
import styles from '../styles.module.scss';
import type {
  LayoutLinksVerticalStateGroupsProps,
  LayoutLinksVerticalStateLinkProps,
  LayoutLinksVerticalStateProps,
  StateCardLink,
} from './types';

export const Group: React.FC<{
  label: string;
  children: React.ReactNode;
}> = (props) => {
  return (
    <div className={styles.groupContainer}>
      <div className={styles.groupHeading}>
        {props.label}
        <hr />
      </div>
      {props.children}
    </div>
  );
};

export const LayoutLinksVerticalStateLink: React.FC<
  LayoutLinksVerticalStateLinkProps
> = ({ link, isActive, onClick }) => {
  return (
    <div
      className={classnames(styles.verticalLink, {
        active: isActive,
      })}
      onClick={onClick}
      data-public
      style={{ cursor: 'pointer' }}
    >
      <Icon name={link.icon || undefined} />
      {link.children}
      {link.premiumFeature && (
        <PremiumFeatureLockIcon
          premiumFeature={link.premiumFeature}
        />
      )}
    </div>
  );
};

export const LayoutLinksVerticalState: React.FC<
  LayoutLinksVerticalStateProps
> = ({ links, groups }) => {
  // Create a flat list of all links with their array indices as unique identifiers
  const allLinks = [
    ...links,
    ...groups.flatMap(
      (group: LayoutLinksVerticalStateGroupsProps) => group.links,
    ),
  ];

  // Use the first link (index 0) as the default active link
  const [activeLinkIndex, setActiveLinkIndex] = useState(0);

  const activeLink = allLinks[activeLinkIndex];

  const handleLinkClick = (index: number) => {
    setActiveLinkIndex(index);
  };

  return (
    <Flex grow>
      <div className={styles.verticalContainer}>
        {links.map((link: StateCardLink, index) => (
          <LayoutLinksVerticalStateLink
            key={index}
            link={link}
            isActive={activeLinkIndex === index}
            onClick={() => handleLinkClick(index)}
          />
        ))}
        {groups.map((group: LayoutLinksVerticalStateGroupsProps) => (
          <Group key={group.label} label={group.label}>
            {group.links.map((link: StateCardLink, linkIndex) => {
              const globalIndex =
                links.length +
                groups
                  .slice(0, groups.indexOf(group))
                  .reduce((acc, g) => acc + g.links.length, 0) +
                linkIndex;
              return (
                <LayoutLinksVerticalStateLink
                  key={globalIndex}
                  link={link}
                  isActive={activeLinkIndex === globalIndex}
                  onClick={() => handleLinkClick(globalIndex)}
                />
              );
            })}
          </Group>
        ))}
      </div>
      <div style={{ flexGrow: 1 }}>
        {activeLink?.render ? activeLink.render({}) : null}
      </div>
    </Flex>
  );
};
